import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import './AdminEventsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminEventsPage() {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'highland_games',
    date_from: '',
    date_to: '',
    time_from: '',
    time_to: '',
    venue_name: '',
    address: '',
    postcode: '',
    region: 'Highlands',
    latitude: 57.5,
    longitude: -4.5,
    organizer: '',
    official_link: '',
    ticket_link: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (user && !user.is_admin) {
      navigate('/');
      return;
    }
    
    if (user && user.is_admin) {
      fetchEvents();
    }
  }, [user, isAuthenticated, navigate]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/events?upcoming_only=false`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreateForm(false);
      resetForm();
      fetchEvents();
      alert('Event erfolgreich erstellt!');
    } catch (error) {
      alert('Fehler beim Erstellen: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdate = async (eventId) => {
    try {
      await axios.put(`${API}/events/${eventId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditing(null);
      resetForm();
      fetchEvents();
      alert('Event erfolgreich aktualisiert!');
    } catch (error) {
      alert('Fehler beim Aktualisieren: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Event wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchEvents();
      alert('Event gelöscht!');
    } catch (error) {
      alert('Fehler beim Löschen: ' + (error.response?.data?.detail || error.message));
    }
  };

  const startEdit = (event) => {
    setEditing(event.id);
    setFormData({
      title: event.title,
      description: event.description,
      category: event.category,
      date_from: event.date_from,
      date_to: event.date_to,
      time_from: event.time_from || '',
      time_to: event.time_to || '',
      venue_name: event.venue_name,
      address: event.address,
      postcode: event.postcode,
      region: event.region,
      latitude: event.latitude,
      longitude: event.longitude,
      organizer: event.organizer,
      official_link: event.official_link || '',
      ticket_link: event.ticket_link || ''
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setShowCreateForm(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'highland_games',
      date_from: '',
      date_to: '',
      time_from: '',
      time_to: '',
      venue_name: '',
      address: '',
      postcode: '',
      region: 'Highlands',
      latitude: 57.5,
      longitude: -4.5,
      organizer: '',
      official_link: '',
      ticket_link: ''
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Lade Events...</p>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return (
      <div className="admin-denied">
        <h2>Zugriff verweigert</h2>
        <p>Nur Administratoren haben Zugriff auf diese Seite.</p>
      </div>
    );
  }

  return (
    <div className="admin-events-page">
      <header className="admin-header">
        <div>
          <h1>
            <Calendar size={32} />
            Event-Verwaltung
          </h1>
          <p>Verwalte alle Highland Games & Military Tattoo Events</p>
        </div>
        <button onClick={() => setShowCreateForm(true)} className="btn-create">
          <Plus size={20} />
          Neues Event
        </button>
      </header>

      {(showCreateForm || editing) && (
        <div className="event-form-modal">
          <div className="event-form-container">
            <div className="form-header">
              <h2>{editing ? 'Event bearbeiten' : 'Neues Event erstellen'}</h2>
              <button onClick={cancelEdit} className="btn-close">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={editing ? (e) => {e.preventDefault(); handleUpdate(editing);} : handleCreate} className="event-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Titel *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Kategorie *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option value="highland_games">Highland Games</option>
                    <option value="military_tattoo">Military Tattoo</option>
                    <option value="combo">Kombi-Event</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Beschreibung *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Datum von *</label>
                  <input
                    type="date"
                    value={formData.date_from}
                    onChange={(e) => setFormData({...formData, date_from: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Datum bis *</label>
                  <input
                    type="date"
                    value={formData.date_to}
                    onChange={(e) => setFormData({...formData, date_to: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Uhrzeit von</label>
                  <input
                    type="time"
                    value={formData.time_from}
                    onChange={(e) => setFormData({...formData, time_from: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Uhrzeit bis</label>
                  <input
                    type="time"
                    value={formData.time_to}
                    onChange={(e) => setFormData({...formData, time_to: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Venue Name *</label>
                  <input
                    type="text"
                    value={formData.venue_name}
                    onChange={(e) => setFormData({...formData, venue_name: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Region *</label>
                  <select
                    value={formData.region}
                    onChange={(e) => setFormData({...formData, region: e.target.value})}
                  >
                    <option value="Highlands">Highlands</option>
                    <option value="Islands">Islands</option>
                    <option value="Borders">Borders</option>
                    <option value="Cities">Cities</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Adresse *</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Postcode *</label>
                  <input
                    type="text"
                    value={formData.postcode}
                    onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Latitude *</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.latitude}
                    onChange={(e) => setFormData({...formData, latitude: parseFloat(e.target.value)})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Longitude *</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.longitude}
                    onChange={(e) => setFormData({...formData, longitude: parseFloat(e.target.value)})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Veranstalter *</label>
                <input
                  type="text"
                  value={formData.organizer}
                  onChange={(e) => setFormData({...formData, organizer: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Offizielle Website</label>
                  <input
                    type="url"
                    value={formData.official_link}
                    onChange={(e) => setFormData({...formData, official_link: e.target.value})}
                    placeholder="https://..."
                  />
                </div>

                <div className="form-group">
                  <label>Ticket Link</label>
                  <input
                    type="url"
                    value={formData.ticket_link}
                    onChange={(e) => setFormData({...formData, ticket_link: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={cancelEdit} className="btn-cancel">
                  Abbrechen
                </button>
                <button type="submit" className="btn-save">
                  <Save size={18} />
                  {editing ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="events-table">
        <table>
          <thead>
            <tr>
              <th>Titel</th>
              <th>Kategorie</th>
              <th>Datum</th>
              <th>Ort</th>
              <th>Region</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td><strong>{event.title}</strong></td>
                <td>{event.category === 'highland_games' ? 'Highland Games' : 'Military Tattoo'}</td>
                <td>{event.date_from}</td>
                <td>{event.venue_name}</td>
                <td>{event.region}</td>
                <td className="actions">
                  <button onClick={() => startEdit(event)} className="btn-edit">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDelete(event.id)} className="btn-delete">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {events.length === 0 && (
          <div className="no-events">
            <p>Noch keine Events vorhanden</p>
          </div>
        )}
      </div>
    </div>
  );
}
